import { validateSharedFreeTextContactField } from "@/lib/listing/listingFreeTextPolicy";
import {
  normalizeItemRequestNotes,
  ITEM_REQUEST_NOTES_MAX_LEN,
} from "./composeRequestTitle";
import {
  ITEM_REQUEST_MAX_PRICE_MIN,
  ITEM_REQUEST_MAX_PRICE_MAX,
} from "./validateItemRequestCapture";

const URGENCIES = new Set(["low", "medium", "high"]);

export type ItemRequestEditInput = {
  urgency: string;
  maxPrice?: number | null;
  notes?: string | null;
};

export type ItemRequestEditValid = {
  ok: true;
  description: string;
  urgency: "low" | "medium" | "high";
  maxPrice: number | undefined;
};

export type ItemRequestEditInvalid = {
  ok: false;
  error: string;
  code:
    | "invalid_urgency"
    | "invalid_max_price"
    | "notes_too_long"
    | "spam_notes";
};

export type ItemRequestEditResult =
  | ItemRequestEditValid
  | ItemRequestEditInvalid;

/**
 * Owner edit validation — same notes/price/urgency gates as create.
 * Title / category / subcategory stay locked on the row.
 */
export function validateItemRequestEdit(
  input: ItemRequestEditInput,
): ItemRequestEditResult {
  const urgency = (input.urgency || "").trim().toLowerCase();

  if (!URGENCIES.has(urgency)) {
    return {
      ok: false,
      error: "Please choose how soon you need it.",
      code: "invalid_urgency",
    };
  }

  let maxPrice: number | undefined;
  if (input.maxPrice != null && input.maxPrice !== undefined) {
    const n = Number(input.maxPrice);
    if (
      !Number.isFinite(n) ||
      n < ITEM_REQUEST_MAX_PRICE_MIN ||
      n > ITEM_REQUEST_MAX_PRICE_MAX
    ) {
      return {
        ok: false,
        error: `Maximum price must be between ₦${ITEM_REQUEST_MAX_PRICE_MIN.toLocaleString("en-NG")} and ₦${ITEM_REQUEST_MAX_PRICE_MAX.toLocaleString("en-NG")}.`,
        code: "invalid_max_price",
      };
    }
    maxPrice = Math.round(n);
  }

  const rawNotes = input.notes ?? "";
  if (rawNotes.trim().length > ITEM_REQUEST_NOTES_MAX_LEN) {
    return {
      ok: false,
      error: `Notes must be at most ${ITEM_REQUEST_NOTES_MAX_LEN} characters.`,
      code: "notes_too_long",
    };
  }

  // H13 — same contact/link gate as listing free-text (buyer-facing copy).
  const notesPolicyError = validateSharedFreeTextContactField(rawNotes, {
    audience: "request",
  });
  if (notesPolicyError) {
    return {
      ok: false,
      error: notesPolicyError,
      code: "spam_notes",
    };
  }

  return {
    ok: true,
    description: normalizeItemRequestNotes(rawNotes),
    urgency: urgency as "low" | "medium" | "high",
    maxPrice,
  };
}
